import { authenticatedFetch } from "./authenticatedFetch";
import { APP_BUILD_ID } from "./appVersion";

/**
 * Proteção global contra telas brancas: captura falhas fatais de renderização
 * (`GlobalErrorBoundary`), erros não tratados (`window.onerror`) e promises
 * rejeitadas sem `.catch` (`unhandledrejection`), e envia um relatório mínimo
 * para diagnóstico. `buildId` usa o mesmo `APP_BUILD_ID` embutido no bundle
 * pelo `craco.config.js` (não consulta `/version.json` em tempo de erro, pois
 * isso mostraria a build publicada no servidor, não a que está de fato
 * executando no dispositivo).
 *
 * O endpoint exige o mesmo token Cognito das demais rotas `api/` (nenhuma
 * fica aberta sem credencial — ver `test_every_api_route_rejects_requests_
 * without_a_cognito_token`). `navigator.sendBeacon` não permite anexar um
 * cabeçalho `Authorization`, então o envio usa `authenticatedFetch` com
 * `keepalive: true`, o mesmo padrão já usado por
 * `workOrders/clientDiagnostics.ts`. Um erro antes do login (sem sessão) não
 * é persistido no servidor; a tela de recuperação e o log local continuam
 * funcionando normalmente.
 */

const API_BASE = process.env.REACT_APP_API_URL ?? "/api";
const CLIENT_ERROR_ENDPOINT = `${API_BASE}/client-errors`;
const CLIENT_ERROR_DEDUP_WINDOW_MS = 10_000;

export type GlobalErrorReportType = "react" | "window" | "unhandledrejection";

export interface GlobalErrorReportInput {
  type: GlobalErrorReportType;
  message: string;
  stack: string | null;
  componentStack?: string | null;
}

export interface GlobalErrorReportPayload {
  type: GlobalErrorReportType;
  message: string;
  stack: string | null;
  componentStack: string | null;
  path: string | null;
  userAgent: string | null;
  buildId: string;
  timestamp: string;
}

let lastFingerprint: string | null = null;
let lastFingerprintAt = 0;

function getLocationPathname() {
  if (typeof window === "undefined") return null;
  try {
    return window.location.pathname || null;
  } catch {
    return null;
  }
}

function getUserAgent() {
  if (typeof navigator === "undefined") return null;
  try {
    return navigator.userAgent || null;
  } catch {
    return null;
  }
}

/** Reconhece a marca `; wv)` que o WebView do Android adiciona ao user agent. */
export function isAndroidWebViewUserAgent(userAgent: string | null | undefined) {
  return Boolean(userAgent && /; ?wv\)/i.test(userAgent));
}

/**
 * Erros de API já tratados pela aplicação (HTTP 4xx/5xx, validações) carregam
 * um `status` numérico nas classes de erro de domínio (`InspectionApiError`,
 * `WorkOrdersApiError`, `CurrentSessionError`, etc.). Quando presente, o fluxo
 * que lançou o erro já decide como exibir a falha ao usuário — reportar aqui
 * de novo só duplicaria ruído sem ajudar o diagnóstico de tela branca.
 */
export function isHandledApiError(value: unknown): boolean {
  if (!(value instanceof Error)) return false;
  const status = (value as { status?: unknown }).status;
  return typeof status === "number" && status >= 400 && status < 600;
}

function buildFingerprint(input: GlobalErrorReportInput) {
  return `${input.type}::${input.message}::${(input.stack ?? "").slice(0, 500)}`;
}

function shouldSuppressRepeatedReport(fingerprint: string) {
  const now = Date.now();
  if (
    lastFingerprint === fingerprint &&
    now - lastFingerprintAt < CLIENT_ERROR_DEDUP_WINDOW_MS
  ) {
    return true;
  }
  lastFingerprint = fingerprint;
  lastFingerprintAt = now;
  return false;
}

function sendGlobalErrorReport(payload: GlobalErrorReportPayload) {
  try {
    void authenticatedFetch(CLIENT_ERROR_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  } catch {
    // Falha no envio da telemetria nunca deve afetar a recuperação do usuário.
  }
}

export function reportGlobalError(
  input: GlobalErrorReportInput,
): GlobalErrorReportPayload | null {
  try {
    const fingerprint = buildFingerprint(input);
    if (shouldSuppressRepeatedReport(fingerprint)) {
      return null;
    }

    const payload: GlobalErrorReportPayload = {
      type: input.type,
      message: input.message,
      stack: input.stack,
      componentStack: input.componentStack ?? null,
      path: getLocationPathname(),
      userAgent: getUserAgent(),
      buildId: APP_BUILD_ID,
      timestamp: new Date().toISOString(),
    };

    console.error("[app] client_error", payload);
    sendGlobalErrorReport(payload);
    return payload;
  } catch {
    return null;
  }
}

let listenersInitialized = false;

/** Reseta o estado de deduplicação e dos listeners; uso exclusivo dos testes. */
export function resetGlobalErrorReportingForTests() {
  lastFingerprint = null;
  lastFingerprintAt = 0;
  listenersInitialized = false;
}

function errorFromWindowEvent(event: ErrorEvent): { message: string; stack: string | null } {
  if (event.error instanceof Error) {
    return { message: event.error.message, stack: event.error.stack ?? null };
  }
  return { message: event.message || "Erro desconhecido", stack: null };
}

function errorFromRejectionEvent(
  event: PromiseRejectionEvent,
): { message: string; stack: string | null } {
  const { reason } = event;
  if (reason instanceof Error) {
    return { message: reason.message, stack: reason.stack ?? null };
  }
  return { message: String(reason ?? "Rejeição sem motivo"), stack: null };
}

/**
 * Registra os listeners globais uma única vez. Deve ser chamado o mais cedo
 * possível (antes do `root.render`) para não perder erros durante o
 * carregamento inicial do app.
 */
export function initGlobalErrorListeners() {
  if (typeof window === "undefined" || listenersInitialized) return;
  listenersInitialized = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    if (isHandledApiError(event.error)) return;
    const { message, stack } = errorFromWindowEvent(event);
    reportGlobalError({ type: "window", message, stack });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    if (isHandledApiError(event.reason)) return;
    const { message, stack } = errorFromRejectionEvent(event);
    reportGlobalError({ type: "unhandledrejection", message, stack });
  });
}
